import { z } from 'zod';
import { feeReminderScheduleSchema } from './fee-reminders.js';
import { dateStringSchema, isoDateTimeSchema } from './common.js';

export const moneyAmountSchema = z
  .number()
  .int('Amount must be in paise (no fractions)')
  .min(0, 'Amount cannot be negative')
  .max(100_000_000, 'Amount exceeds limit');

export const feeCycleCreateSchema = z
  .object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    dueDate: dateStringSchema,
    notes: z.string().max(1000).optional(),
  })
  .strict();

export const feeStructureSchema = z
  .object({
    programSlug: z.string().min(2).max(60),
    mode: z.enum(['online', 'offline', 'hybrid']),
    amountPaise: moneyAmountSchema,
  })
  .strict();

export const feePaymentMarkPaidSchema = z
  .object({
    paymentId: z.string().uuid(),
    paidAt: isoDateTimeSchema,
    paidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
    reference: z.string().max(200).optional(),
    notes: z.string().max(500).optional(),
  })
  .strict();

export const feePaymentBulkMarkPaidSchema = z
  .object({
    paymentIds: z.array(z.string().uuid()).min(1).max(100),
    paidAt: isoDateTimeSchema,
    paidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
    notes: z.string().max(500).optional(),
  })
  .strict();

export const feePaymentClaimSchema = z
  .object({
    paymentId: z.string().uuid(),
    claimedPaidAt: isoDateTimeSchema,
    claimedPaidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
    reference: z.string().max(200).optional(),
    notes: z.string().max(500).optional(),
  })
  .strict();

// Cycle PATCH — year/month never editable because of the unique index; only
// dueDate and notes can be changed once a cycle exists.
export const feeCycleUpdateSchema = z
  .object({
    dueDate: dateStringSchema.optional(),
    notes: z.string().max(1000).nullish(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided',
  });

// Add a single student to a cycle — used by the cycle detail page's
// "Add student" relation modal. `discountPaise` is optional; the server
// derives `netAmountPaise = amountPaise - discountPaise`.
export const feeCyclePaymentAddSchema = z
  .object({
    studentId: z.string().uuid(),
    amountPaise: z
      .number()
      .int('Amount must be in paise (no fractions)')
      .positive('Amount must be greater than zero')
      .max(100_000_000, 'Amount exceeds limit'),
    discountPaise: moneyAmountSchema.optional(),
  })
  .strict();

// Claim PATCH — pending claims only; status transitions go through the
// dedicated /confirm and /reject endpoints. Mirrors the create schema but
// drops `paymentId` (immutable) and `status` (workflow-only).
export const feeClaimUpdateSchema = z
  .object({
    claimedPaidAt: isoDateTimeSchema.optional(),
    claimedPaidVia: z
      .enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other'])
      .optional(),
    reference: z.string().max(200).nullish(),
    notes: z.string().max(500).nullish(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided',
  });

export const salaryCycleCreateSchema = z
  .object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    payoutDate: dateStringSchema,
    notes: z.string().max(1000).optional(),
  })
  .strict();

export const salaryPaymentMarkPaidSchema = z
  .object({
    paymentId: z.string().uuid(),
    paidAt: isoDateTimeSchema,
    paidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
    reference: z.string().max(200).optional(),
    notes: z.string().max(500).optional(),
  })
  .strict();

/** POST /api/salaries/cycles/:id/mark-all-paid — every pending, non-zero payment in the cycle. */
export const salaryCycleMarkAllPaidSchema = z
  .object({
    paidAt: isoDateTimeSchema.optional(),
    paidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']).default('bank_transfer'),
  })
  .strict();

// Salary cycle PATCH — year/month never editable because of the unique index;
// only payoutDate and notes can be changed once a cycle exists.
export const salaryCycleUpdateSchema = z
  .object({
    payoutDate: dateStringSchema.optional(),
    notes: z.string().max(1000).nullish(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided',
  });

// Add a single ad-hoc payment to a cycle — used by the cycle detail page's
// "Add ad-hoc payment" relation modal. Exactly one of `teacherId` /
// `managerId` must be supplied so the row is unambiguously typed; the server
// derives `role` from whichever side is set and computes
// `netPaise = grossPaise - deductionsPaise`.
export const salaryCyclePaymentAddSchema = z
  .object({
    teacherId: z.string().uuid().optional(),
    managerId: z.string().uuid().optional(),
    grossPaise: z
      .number()
      .int('Amount must be in paise (no fractions)')
      .positive('Amount must be greater than zero')
      .max(100_000_000, 'Amount exceeds limit'),
    deductionsPaise: moneyAmountSchema.optional(),
    notes: z.string().max(500).optional(),
  })
  .strict()
  /**
   * XOR refinement: exactly one of `teacherId` or `managerId` must be set.
   * Both-missing and both-present are rejected so callers cannot create an
   * orphan row or a row that points at two payees simultaneously.
   */
  .superRefine((data, ctx) => {
    const hasTeacher = data.teacherId !== undefined;
    const hasManager = data.managerId !== undefined;
    if (hasTeacher === hasManager) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teacherId'],
        message: 'Exactly one of teacherId or managerId must be provided',
      });
    }
  });

// PATCH /api/salaries/payments/:id/amounts — adjust the salary breakdown
// while the row is still pending or held. The server recomputes
// `netPaise = grossPaise - deductionsPaise` and persists the trio plus
// optional notes. Rejected when the payment has already been paid.
export const salaryPaymentAmountsSchema = z
  .object({
    grossPaise: z
      .number()
      .int('Amount must be in paise (no fractions)')
      .positive('Amount must be greater than zero')
      .max(100_000_000, 'Amount exceeds limit'),
    deductionsPaise: moneyAmountSchema,
    notes: z.string().max(500).nullish(),
  })
  .strict();

// Hold body — used for both cycle-hold and payment-hold endpoints. Reason
// is required so the audit trail records why a payout was stalled.
export const salaryCycleHoldSchema = z
  .object({
    reason: z
      .string()
      .trim()
      .min(2, 'Reason is too short')
      .max(500, 'Reason is too long'),
  })
  .strict();

export const systemSettingsUpdateSchema = z
  .object({
    autoGenerateFeeCyclesEnabled: z.boolean().optional(),
    autoGenerateFeeCyclesDayOfMonth: z.number().int().min(1).max(28).optional(),
    autoGenerateSalaryCyclesEnabled: z.boolean().optional(),
    autoGenerateSalaryCyclesDayOfMonth: z.number().int().min(1).max(28).optional(),
    autoFeeRemindersEnabled: z.boolean().optional(),
    autoFeeReminderSchedule: feeReminderScheduleSchema.optional(),
    onlinePaymentsEnabled: z.boolean().optional(),
    onlinePaymentsProvider: z.enum(['none', 'razorpay']).optional(),
    siblingDiscountPercent: z.number().int().min(0).max(50).optional(),
    lateFeeAmountPaise: moneyAmountSchema.optional(),
    lateFeeGraceDays: z.number().int().min(0).max(30).optional(),
    feeDueDayOfMonth: z.number().int().min(1).max(28).optional(),
    automaticEmailsEnabled: z.boolean().optional(),
  })
  .strict();

export type FeeCycleInput = z.infer<typeof feeCycleCreateSchema>;
export type FeeCycleUpdateInput = z.infer<typeof feeCycleUpdateSchema>;
export type FeeCyclePaymentAddInput = z.infer<typeof feeCyclePaymentAddSchema>;
export type FeeClaimUpdateInput = z.infer<typeof feeClaimUpdateSchema>;
export type FeePaymentMarkPaidInput = z.infer<typeof feePaymentMarkPaidSchema>;
export type SalaryCycleInput = z.infer<typeof salaryCycleCreateSchema>;
export type SalaryCycleUpdateInput = z.infer<typeof salaryCycleUpdateSchema>;
export type SalaryCyclePaymentAddInput = z.infer<typeof salaryCyclePaymentAddSchema>;
export type SalaryPaymentAmountsInput = z.infer<typeof salaryPaymentAmountsSchema>;
export type SalaryCycleHoldInput = z.infer<typeof salaryCycleHoldSchema>;
export type SystemSettingsUpdate = z.infer<typeof systemSettingsUpdateSchema>;
